import { ReactElement, useEffect } from 'react';
import { months, years } from 'utils/dateTimes';

import { Component as LockIcon } from 'icons/icon-ui--lock.svg';
import { Component as CaretIcon } from 'icons/icon-ui--caret.svg';
import { Component as MastercardIcon } from 'payment-img/credit-card/mastercard.svg';
import { Component as DiscoverIcon } from 'payment-img/credit-card/discover.svg';
import { Component as AmericanExpressIcon } from 'payment-img/credit-card/american_express.svg';
import { Component as VisaIcon } from 'payment-img/credit-card/visa.svg';

import dSrc from 'payment-img/credit-card/discover.png';
import dSrc2 from 'payment-img/credit-card/discover@2x.png';
import mcSrc from 'payment-img/credit-card/mastercard.png';
import mcSrc2 from 'payment-img/credit-card/mastercard@2x.png';
import visaSrc from 'payment-img/credit-card/visa.png';
import visaSrc2 from 'payment-img/credit-card/visa@2x.png';
import cvcSrc from 'payment-img/credit-card/cvc-graphics.png';
import cvcSrc2 from 'payment-img/credit-card/cvc-graphics@2x.png';
import aeSrc from 'payment-img/credit-card/american_express.png';
import aeSrc2 from 'payment-img/credit-card/american_express@2x.png';
import useCurrentUser from '../../../hooks/useCurrentUser';

type Props = {
  show: boolean;
  error: string;
  number: string;
  expMonth: string;
  expYear: string;
  cvc: string;
  setNumber: (number: string) => void;
  setExpMonth: (expMonth: string) => void;
  setExpYear: (expYear: string) => void;
  setCvc: (cvc: string) => void;
};

function CreditCardProvider({
  show,
  error,
  number,
  expMonth,
  expYear,
  cvc,
  setNumber,
  setExpMonth,
  setExpYear,
  setCvc,
}: Props): ReactElement {
  const { currentUser } = useCurrentUser();
  const { payment_method: paymentMethod } = currentUser;
  const inactiveUser = currentUser?.subscription?.status === 'inactive';
  const isAccountManagementEnabled = window.gon?.featureFlags?.isAccountManagementEnabled;
  const digitsOnlyRegExp = /^\d*$/;
  const fourDigitRegExp = /\d{1,4}/g;
  const blankSpaceRegExp = /\s+/g;

  const creditcards = [
    {
      brand: 'American Express',
      verification: '^3[47][0-9]',
      separation: '^([0-9]{4})([0-9]{6})?(?:([0-9]{6})([0-9]{5}))?$',
    },
    {
      brand: 'MasterCard',
      verification: '^5[1-5][0-9]',
      separation: '^([0-9]{4})([0-9]{4})?([0-9]{4})?([0-9]{4})?$',
    },
    {
      brand: 'Visa',
      verification: '^4[0-9]',
      separation: '^([0-9]{4})([0-9]{4})?([0-9]{4})?([0-9]{4})?$',
    },
    {
      brand: 'Discover',
      verification: '^6(?:011|5[0-9]{2})[0-9]',
      separation: '^([0-9]{4})([0-9]{4})?([0-9]{4})?([0-9]{4})?$',
    },
  ];

  useEffect(() => {
    if (paymentMethod) {
      setNumber(`**** **** **** ${paymentMethod.cc_last_four}`);
      setCvc(paymentMethod.cc_brand === 'American Express' ? '****' : '***');
      setExpMonth(paymentMethod.cc_exp_month);
      setExpYear(paymentMethod.cc_exp_year);
    }
  }, []);
  const handleCreditCardChange = (e) => {
    let groups = null;
    for (let i = 0; i < creditcards.length; i++) {
      const cardNumber = e
        .target
        .value
        .replace(blankSpaceRegExp, '');
      const re = new RegExp(creditcards[i].verification);
      // verify the brand
      if (cardNumber.match(re) != null) {
        const cardRegexGroups = new RegExp(creditcards[i].separation).exec(cardNumber);
        if (cardRegexGroups != null && cardRegexGroups.length > 1) {
          // separate out the numbers into appropriate groups starting from index 1 according
          // to the brand's separation regex
          // Ex: ['371449635398431', '3714', undefined, '496353', '98431']
          // @ts-expect-error TODO: fix null safety
          groups = cardRegexGroups.slice(1, cardRegexGroups.length)
            .filter((item) => item !== undefined);
        }
        break;
      }
    }
    // @ts-expect-error TODO: fix null safety
    if (groups == null || groups.length === 0) {
      groups = e
        .target
        .value
        .replace(blankSpaceRegExp, '')
        .match(fourDigitRegExp);
    }

    // @ts-expect-error TODO: fix null safety
    setNumber(groups ? groups.join(' ') : '');
  };

  const handleCvcChange = (e) => {
    if (!digitsOnlyRegExp.test(e.target.value)) {
      e.preventDefault();
    } else {
      setCvc(e.target.value);
    }
  };
  const handleCreditCardFocus = () => {
    if (number.startsWith('*')) {
      setNumber('');
    }
  };
  const handleCvcFocus = () => {
    if (cvc.startsWith('*')) {
      setCvc('');
    }
  };

  return (
    <div
      id="payment-method-cc"
      aria-labelledby="payment-button-cc"
      className={`c-payment-form__body collapse ${show ? 'show' : ''}`}
    >
      <div className="c-payment-form__info c-payment-form__info--sm">
        Your payment info will be stored securely&nbsp;
        <LockIcon height="16" />
      </div>

      {!inactiveUser && isAccountManagementEnabled && (
      <div className="c-payment-form__card-types">
        <VisaIcon />
        <MastercardIcon />
        <DiscoverIcon />
        <AmericanExpressIcon />
      </div>
      )}
      {error && (
        <div className="c-payment-form__errors">
          <div className="c-alert c-alert--error" role="alertdialog">
            <div className="c-alert__text">{error}</div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-xs-12 col-md-9">
          <div className="c-form-group">
            <label htmlFor="cc_number">
              Card number&nbsp;
              <span className="sr-only">
                (available cards: Visa, Mastercard, Discovery, American Express)
              </span>
              <sup>*</sup>
            </label>
            <input
              id="cc_number"
              type="text"
              aria-label="Card number"
              size={255}
              maxLength={19}
              value={number}
              onChange={handleCreditCardChange}
              onFocus={handleCreditCardFocus}
            />
          </div>
        </div>

        { inactiveUser && (
        <div className="col-2 c-payment-form__card-brands">
          <img alt="" srcSet={`${visaSrc2} 2x`} src={visaSrc} />
          <img alt="" srcSet={`${mcSrc2} 2x`} src={mcSrc} />
          <img alt="" srcSet={`${dSrc2} 2x`} src={dSrc} />
          <img alt="" srcSet={`${aeSrc2} 2x`} src={aeSrc} />
        </div>
        )}
      </div>

      <div className="row">
        <div className={[isAccountManagementEnabled && !inactiveUser ? 'col-12' : 'col-xs-12 col-md-8'].join('')}>
          <div className="row" style={{ marginTop: '16px' }}>
            <div className="col-12">
              <legend>
                Expiration date&nbsp;
                <sup>*</sup>
              </legend>
            </div>
            <div className="col-6">
              <div className="c-form-group">
                <div className="c-input-group c-input-group--icon-right">
                  <select
                    id="cc-exp-month"
                    aria-label="Expiration month"
                    value={expMonth}
                    onChange={(e) => setExpMonth(e.target.value)}
                  >
                    <option value="" hidden>Month</option>
                    {months().map((val) => (
                      <option key={val} value={val}>{val}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="c-link c-input-icon c-input-icon--right"
                    aria-label="expand month selection"
                  >
                    <CaretIcon
                      className="c-select__caret-icon c-svg-fill-gray-115-color"
                      height="12"
                      width="12"
                    />
                  </button>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="c-form-group">
                <div className="c-input-group c-input-group--icon-right">
                  <select
                    id="cc-exp-year"
                    aria-label="Expiration year"
                    value={expYear}
                    onChange={(e) => setExpYear(e.target.value)}
                  >
                    <option value="" hidden>Year</option>
                    {years(20).map((val) => (
                      <option key={val} value={val}>{val}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="c-link c-input-icon c-input-icon--right"
                    aria-label="expand year selection"
                  >
                    <CaretIcon
                      className="c-select__caret-icon c-svg-fill-gray-115-color"
                      height="12"
                      width="12"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={[isAccountManagementEnabled && !inactiveUser ? 'col-12' : 'col-12 col-md-4'].join('')}>
          <div className="row" style={{ marginTop: '16px' }}>
            <label className="col-12" htmlFor="cvc_code">
              CVC Code&nbsp;
              <sup>*</sup>
            </label>
            <div className="col-6 col-md-12">
              <input
                id="cvc_code"
                aria-label="CVC Code"
                type="tel"
                maxLength={4}
                value={cvc}
                onChange={handleCvcChange}
                onFocus={handleCvcFocus}
              />
            </div>
            {(inactiveUser && isAccountManagementEnabled) && (
              <div className="col-6 d-block d-md-none">
                <img
                  className="c-payment-form__cvc"
                  alt={[
                    'CVC is the 3-4 digit security code ',
                    'on the front or back of your card.',
                  ].join()}
                  srcSet={`${cvcSrc2} 2x`}
                  src={cvcSrc}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="c-payment-form__required mt-3">
        *Required Fields
      </div>
    </div>
  );
}

export default CreditCardProvider;
